import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Hammer, DoorOpen, Droplets, Zap, Paintbrush, ThermometerSun,
  Shield, Clock, Award, ChevronRight, Star, ArrowRight, Quote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeIn = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } } };
const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

const services = [
  { icon: Hammer, title: "Maçonnerie", desc: "Gros œuvre, ouverture de murs porteurs et restructuration complète pour transformer vos volumes en toute sécurité." },
  { icon: DoorOpen, title: "Menuiserie", desc: "Aménagements sur-mesure, pose de parquets et rénovation de vos fenêtres pour allier esthétique et praticité." },
  { icon: Droplets, title: "Plomberie", desc: "Création et rénovation complète de vos salles de bain et cuisines, avec une installation fiable et durable." },
  { icon: Zap, title: "Electricité", desc: "Mise en sécurité totale de votre installation et pose d'éclairages modernes pour un confort optimal au quotidien." },
  { icon: Paintbrush, title: "Peinture", desc: "Préparation minutieuse des supports et finitions soignées pour sublimer vos murs et plafonds avec des matériaux de qualité." },
  { icon: ThermometerSun, title: "Isolation", desc: "Optimisation de votre confort thermique et acoustique pour une maison saine, calme et économe en énergie." },
];

const galleryImages = [
  { url: "https://images.unsplash.com/photo-1505409628601-edc9af17fda6?w=800&q=80&fm=webp", alt: "Appartement haussmannien rénové", span: true },
  { url: "https://images.unsplash.com/photo-1592229506151-845940174bb0?w=600&q=80&fm=webp", alt: "Salon moderne rénové" },
  { url: "https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?w=600&q=80&fm=webp", alt: "Intérieur parisien élégant" },
  { url: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80&fm=webp", alt: "Cuisine rénovée haut de gamme" },
  { url: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=600&q=80&fm=webp", alt: "Salle de bain design" },
];

const reassurance = [
  { icon: Clock, value: "26", label: "Années d'expérience", suffix: "ans" },
  { icon: Award, value: "500", label: "Projets réalisés", suffix: "+" },
  { icon: Shield, value: "100", label: "Satisfaction client", suffix: "%" },
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then((res) => setTestimonials(res.data)).catch(console.error);
  }, []);

  return (
    <div data-testid="home-page">
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1505409628601-edc9af17fda6?w=1600&q=80&fm=webp')` }} />
        <div className="absolute inset-0 bg-black/50 z-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeIn} className="text-sm tracking-[0.3em] uppercase text-[#C5A065] font-medium mb-6">
              Artisan depuis 2000 &mdash; Paris & Ile-de-France
            </motion.p>
            <motion.h1 variants={fadeIn} className="font-heading text-5xl md:text-7xl font-medium tracking-tight text-white leading-[1.1]" data-testid="hero-h1">
              Expert en rénovation d'appartements.
            </motion.h1>
            <motion.div variants={fadeIn} className="section-divider mt-8 mb-8 bg-[#C5A065]" />
            <motion.p variants={fadeIn} className="text-lg md:text-xl font-light leading-relaxed text-white/90 max-w-xl">
              Depuis 2000, nous mettons notre savoir-faire au service des intérieurs exigeants. 
              Rénovation complète, suivi rigoureux et résultat garanti (promis, on range nos outils avant de partir).
            </motion.p>
            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-4 mt-12">
              <Link to="/contact">
                <Button className="group bg-[#C5A065] text-white hover:bg-[#C5A065]/90 px-8 py-6 text-sm tracking-widest uppercase rounded-none" data-testid="hero-cta-primary">
                  Demander un devis
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-white/50 text-white hover:bg-white hover:text-[#2C2C2C] px-8 py-6 text-sm tracking-widest uppercase rounded-none" data-testid="hero-cta-secondary">
                  Nos services
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white border-y border-border/50" data-testid="reassurance-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {reassurance.map((item, i) => (
              <motion.div key={i} variants={fadeIn} className="text-center">
                <item.icon className="w-8 h-8 text-[#C5A065] mx-auto mb-4 stroke-[1.5]" />
                <p className="font-heading text-5xl font-medium text-[#2C2C2C]">{item.value}<span className="text-[#C5A065] text-3xl">{item.suffix}</span></p>
                <p className="text-sm tracking-widest uppercase text-[#8A8A8A] mt-2">{item.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.p variants={fadeIn} className="text-sm tracking-[0.3em] uppercase text-[#C5A065] font-medium">Notre expertise</motion.p>
            <motion.h2 variants={fadeIn} className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-[#2C2C2C] mt-4">L'expertise d'un artisan<br />pour tous vos travaux</motion.h2>
            <motion.div variants={fadeIn} className="section-divider mt-6 mb-16" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, i) => (
                <motion.div key={i} variants={fadeIn}>
                  <Card className="bg-white border border-border/50 hover:border-[#C5A065]/50 transition-all duration-300 rounded-none shadow-none hover:shadow-md group h-full">
                    <CardContent className="p-8">
                      <service.icon className="w-8 h-8 text-[#C5A065] stroke-[1.5] mb-6 group-hover:scale-110 transition-transform duration-300" />
                      <h3 className="font-heading text-xl font-medium text-[#2C2C2C] mb-3">{service.title}</h3>
                      <p className="text-sm leading-relaxed text-[#8A8A8A]">{service.desc}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            <motion.div variants={fadeIn} className="mt-12 text-center">
              <Link to="/services">
                <Button variant="outline" className="group border-[#C5A065] text-[#C5A065] hover:bg-[#C5A065] hover:text-white px-8 py-5 text-sm tracking-widest uppercase rounded-none" data-testid="services-cta">
                  Découvrir tous nos services
                  <ChevronRight className="w-4 h-4 ml-1 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#E5E0D8]/30" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.p variants={fadeIn} className="text-sm tracking-[0.3em] uppercase text-[#C5A065] font-medium">Nos réalisations</motion.p>
            <motion.h2 variants={fadeIn} className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-[#2C2C2C] mt-4 mb-12">Découvrez nos dernières réalisations</motion.h2>

            <motion.div variants={fadeIn} className="gallery-grid">
              {galleryImages.map((img, i) => (
                <div key={i} className={`img-zoom ${i === 0 ? "row-span-2" : ""}`} data-testid={`gallery-image-${i}`}>
                  <img src={img.url} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#1A1A1A]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeIn} className="text-sm tracking-[0.3em] uppercase text-[#C5A065] font-medium mb-6">Votre projet commence ici</motion.p>
            <motion.h2 variants={fadeIn} className="font-heading text-4xl md:text-5xl font-normal text-white tracking-tight">Parlons de votre<br />prochain projet</motion.h2>
            <motion.div variants={fadeIn} className="section-divider mx-auto mt-8 mb-8" />
            <motion.p variants={fadeIn} className="text-base text-white/60 max-w-lg mx-auto mb-12">Chaque projet est unique. Contactez-nous pour un diagnostic gratuit et un devis personnalisé, sans engagement.</motion.p>
            <motion.div variants={fadeIn}>
              <Link to="/contact">
                <Button className="group bg-[#C5A065] text-white hover:bg-[#C5A065]/90 px-10 py-6 text-sm tracking-widest uppercase rounded-none" data-testid="cta-button">
                  Prendre rendez-vous
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
